import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ZoomableImage } from "./ZoomableImage";
import { Search, Package, Layers, Edit2, Trash2, Plus, Eye, Camera } from "lucide-react";
import { resolveImageUrl } from "@/lib/api";
import { AuthImg, useAuthedImage } from "./AuthImg";

function lev(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const curr = [i];
    for (let j = 1; j <= n; j++) {
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = curr;
  }
  return prev[n];
}

export function fuzzyScore(query, text) {
  const q = query.toLowerCase().trim(), t = text.toLowerCase();
  if (!q) return 1;
  if (t.includes(q)) return 1;
  const qTokens = q.split(/[\s,x×-]+/).filter(Boolean);
  const tTokens = t.split(/[\s,x×-]+/).filter(Boolean);
  if (qTokens.length === 0 || tTokens.length === 0) return 0;
  let total = 0;
  qTokens.forEach(qt => {
    let best = 0;
    tTokens.forEach(tt => {
      if (tt.includes(qt) || qt.includes(tt)) { best = Math.max(best, 0.9); return; }
      const sim = 1 - lev(qt, tt) / Math.max(qt.length, tt.length);
      best = Math.max(best, sim);
    });
    total += best;
  });
  return total / qTokens.length;
}


export function PalletTypesSidebar({
  palletTypes, selectedPalletType, setSelectedPalletType,
  searchTerm, setSearchTerm, openEditForm, setDeleteConfirmId, openCreateForm,
  officeMode,
}) {
  const [detailsPallet, setDetailsPallet] = useState(null);

  // "View Details" button on the calculator opens the same dialog
  useEffect(() => {
    const h = (e) => { if (e.detail) setDetailsPallet(e.detail); };
    window.addEventListener('view-pallet-details', h);
    return () => window.removeEventListener('view-pallet-details', h);
  }, []);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const detailImages = detailsPallet?.images || (detailsPallet?.image ? [detailsPallet.image] : []);
  const lightboxSrc = useAuthedImage(lightboxIdx !== null ? detailImages[lightboxIdx] : null);

  return (
    <>
    <Card
      className="bg-white border border-zinc-200 rounded-none shadow-none md:row-span-2"
      data-testid="pallet-types-list-card"
    >
      {!officeMode && (
      <div className="px-4 pt-4 pb-3">
        <button
          onClick={openCreateForm}
          className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-orange-400 text-orange-600 hover:bg-orange-50 transition-colors text-sm font-medium"
          data-testid="create-pallet-type-btn"
        >
          <Plus className="w-4 h-4" />
          New Pallet Type
        </button>
      </div>
      )}
      <CardHeader className="px-6 pt-0 pb-4">
        <CardTitle
          className="text-lg font-bold text-zinc-900 tracking-tight flex items-center gap-2"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          <Layers className="w-5 h-5 text-orange-600" />
          Saved Pallet Types
        </CardTitle>
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mt-2">
          {palletTypes.length} types available
        </p>
        <div className="mt-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-white border border-zinc-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-orange-600 h-9 text-sm"
              data-testid="search-pallet-types"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[calc(100vh-400px)] min-h-[250px]">
          {palletTypes.length === 0 ? (
            <div className="p-6 text-center text-zinc-500" data-testid="empty-pallet-types">
              <Package className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
              <p className="text-sm">No pallet types yet</p>
              <p className="text-xs mt-1">Create one to get started</p>
            </div>
          ) : (
            <div className="space-y-0">
              {(() => {
                const q = searchTerm.trim().toLowerCase();
                let list = palletTypes;
                let fuzzy = false;
                if (q) {
                  const exact = palletTypes.filter(pallet =>
                    pallet.name.toLowerCase().includes(q) ||
                    (pallet.description && pallet.description.toLowerCase().includes(q))
                  );
                  if (exact.length > 0) list = exact;
                  else {
                    fuzzy = true;
                    list = palletTypes
                      .map(p => ({ p, s: fuzzyScore(q, `${p.name} ${p.description || ''}`) }))
                      .filter(x => x.s >= 0.45)
                      .sort((a, b) => b.s - a.s)
                      .slice(0, 6)
                      .map(x => x.p);
                  }
                }
                return (
                <>
                {fuzzy && list.length > 0 && (
                  <p className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border-b border-amber-200" data-testid="fuzzy-match-label">
                    No exact match — closest matches:
                  </p>
                )}
                {q && list.length === 0 && (
                  <p className="p-4 text-xs text-zinc-400 text-center" data-testid="no-pallet-matches">
                    Nothing close to &quot;{searchTerm}&quot; found
                  </p>
                )}
                {list.map((pallet) => (
                <div
                  key={pallet.id}
                  className={`p-4 pr-3 cursor-pointer transition-colors duration-200 border-b border-zinc-200 ${
                    selectedPalletType?.id === pallet.id
                      ? "bg-orange-300 border-l-[5px] border-l-orange-600 shadow-lg"
                      : "border-l-[5px] border-l-transparent hover:bg-zinc-50"
                  }`}
                  onClick={() => setSelectedPalletType(pallet)}
                  data-testid={`pallet-type-item-${pallet.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3
                        className="font-semibold text-zinc-900 text-sm break-words"
                        style={{ wordBreak: 'break-word' }}
                        data-testid={`pallet-type-name-${pallet.id}`}
                      >
                        {pallet.name}
                      </h3>
                      <p className="text-xs text-zinc-500 mt-1">
                        {pallet.components.length} components
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {officeMode ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDetailsPallet(pallet); }}
                          className="p-1.5 hover:bg-zinc-200 rounded-none transition-colors"
                          data-testid={`view-details-btn-${pallet.id}`}
                        >
                          <Eye className="w-4 h-4 text-zinc-600" />
                        </button>
                      ) : (
                        <>
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditForm(pallet); }}
                          className="p-1.5 hover:bg-zinc-200 rounded-none transition-colors"
                          data-testid={`edit-pallet-btn-${pallet.id}`}
                        >
                          <Edit2 className="w-4 h-4 text-zinc-600" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(pallet.id); }}
                          className="p-1.5 hover:bg-red-100 rounded-none transition-colors"
                          data-testid={`delete-pallet-btn-${pallet.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                ))}
                </>
                );
              })()}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>

    {/* View Details Dialog */}
    <AlertDialog open={!!detailsPallet} onOpenChange={() => setDetailsPallet(null)}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none max-w-lg max-h-[80vh] overflow-y-auto">
        {detailsPallet && (
        <>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
            {detailsPallet.name}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <div className="space-y-4 text-sm">
          {detailsPallet.description && (
            <p className="text-zinc-500">{detailsPallet.description}</p>
          )}
          {detailImages.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-2">Photos</p>
              <div className="grid grid-cols-3 gap-2">
                {detailImages.map((img, i) => (
                  <AuthImg key={i} src={img} alt={`${detailsPallet.name} ${i + 1}`}
                    className="w-full h-20 object-cover border border-zinc-200 cursor-pointer hover:border-orange-400 transition-colors"
                    onClick={() => setLightboxIdx(i)} />
                ))}
              </div>
            </div>
          )}
          {detailsPallet.notes && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">Notes</p>
              <p className="text-zinc-600 whitespace-pre-wrap">{detailsPallet.notes}</p>
            </div>
          )}
          {(detailsPallet.components || []).map((comp, ci) => (
            <div key={ci} className="border border-zinc-200 p-3">
              <p className="font-bold text-zinc-800 mb-2">{comp.name}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <span className="text-zinc-400">Qty per pallet:</span><span className="text-zinc-700 font-semibold">{comp.quantity}</span>
                {comp.length && <><span className="text-zinc-400">Length:</span><span className="text-zinc-700">{comp.length}&quot;</span></>}
                {comp.height && <><span className="text-zinc-400">Width:</span><span className="text-zinc-700">{comp.height}&quot;</span></>}
                {comp.thickness && <><span className="text-zinc-400">Thick:</span><span className="text-zinc-700">{comp.thickness}&quot;</span></>}
                {comp.pcs_per_min && <><span className="text-zinc-400">Pcs/min:</span><span className="text-zinc-700">{comp.pcs_per_min}</span></>}
                {comp.board_footage && <><span className="text-zinc-400">Bd ft/pc:</span><span className="text-zinc-700">{comp.board_footage}</span></>}
                {comp.notched != null && <><span className="text-zinc-400">Notched:</span><span className="text-zinc-700">{comp.notched ? `Yes${comp.notch_length ? ` (${comp.notch_length}")` : ''}` : 'No'}</span></>}
                {comp.skids && <><span className="text-zinc-400">Pcs/skid:</span><span className="text-zinc-700">{comp.skids}</span></>}
              </div>
              {comp.lumber?.length > 0 && comp.lumber.some(l => l?.length || l?.quantity) && (
                <div className="mt-2 pt-2 border-t border-zinc-100">
                  <p className="text-[10px] uppercase tracking-wider text-violet-500 font-semibold mb-1">Lumber</p>
                  {comp.lumber.filter(l => l?.length || l?.quantity).map((l, li) => (
                    <p key={li} className="text-xs text-violet-700">
                      {l.quantity} pcs/cant — {l.thickness ? `${l.thickness}"` : ''}{l.width && l.width !== 'Random' ? ` x ${l.width}"` : l.width === 'Random' ? ' x Rndm' : ''} x {l.length}&apos;{l.description ? ` ${l.description}` : ''}
                    </p>
                  ))}
                </div>
              )}
              {comp.extras && (
                <div className="mt-2 pt-2 border-t border-zinc-100">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">Extras</p>
                  <p className="text-xs text-zinc-600 whitespace-pre-wrap">{comp.extras}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        </>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Close</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Image Lightbox */}
    {lightboxIdx !== null && detailImages.length > 0 && (
      <Dialog open onOpenChange={(o) => { if (!o) setLightboxIdx(null); }}>
        <DialogContent
          className="rounded-none border-0 bg-black/95 p-3 w-[96vw] max-w-3xl [&>button]:hidden"
          data-testid="pallet-photo-lightbox"
        >
          <DialogTitle className="sr-only">Photo viewer</DialogTitle>
          <div className="flex flex-col items-center">
            <button onClick={() => setLightboxIdx(null)} className="absolute top-2 right-2 z-10 px-3 py-1.5 bg-white/20 text-white hover:bg-white/30 text-sm font-bold" data-testid="lightbox-close">
              Close &times;
            </button>
            <ZoomableImage src={lightboxSrc} alt="" className="max-w-full max-h-[70vh] object-contain" />
            {detailImages.length > 1 && (
              <div className="flex items-center gap-4 mt-3">
                <button onClick={() => setLightboxIdx(i => Math.max(0, i - 1))} disabled={lightboxIdx === 0}
                  className="px-4 py-2 bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 text-sm font-bold" data-testid="lightbox-prev">Prev</button>
                <span className="text-white text-sm font-bold" data-testid="lightbox-counter">{lightboxIdx + 1} / {detailImages.length}</span>
                <button onClick={() => setLightboxIdx(i => Math.min(detailImages.length - 1, i + 1))} disabled={lightboxIdx === detailImages.length - 1}
                  className="px-4 py-2 bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 text-sm font-bold" data-testid="lightbox-next">Next</button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}
